package com.shadowings.gero.domain.model

import kotlinx.serialization.Serializable

enum class QuizQuestionType {
    TRUE_FALSE,
    SINGLE_SELECTION,
    MULTI_SELECTION,
    MULTI_PRESS,
    WORD_SELECTION,
    TEXT_TRANSLATION,
    WORD_SPELLING,
    PHONETIC_KEYBOARD,
    IMAGE_KEYBOARD,
}

@Serializable
data class WrongAnswerRecord(
    val id: String = "",
    val lessonId: String = "",
    val type: QuizQuestionType = QuizQuestionType.SINGLE_SELECTION,
    val questionText: String = "",
    val correctAnswerText: String = "",
    val userAnswerText: String = "",
    val hieroglyphs: String = "",
    val timestamp: String = "0", // String, not Long — see UserData.consumablesUpdatedAt for why
    val attempts: Int = 1,
    // Whether correctAnswerText is a Gardiner code (rendered as a hieroglyph glyph) rather than a
    // translation/transliteration string. No longer read (options below carries the real choices),
    // but kept so already-stored JSON/Firestore documents that contain it still decode.
    val answerIsHieroglyph: Boolean = false,
    // The exact choices the user was shown, already resolved to text: SINGLE_SELECTION's options,
    // WORD_SELECTION's word-bank tiles, WORD_SPELLING's Gardiner blocks (correct ones included).
    // WrongAnswerRecordQuizMapper replays these as-is, so a correction question gets the original
    // distractors instead of guessed ones. Empty on records saved before this field existed.
    val options: List<String> = emptyList(),
    // WORD_SELECTION's other accepted translations (WordSelectionContent.alternativePhrases,
    // e.g. the other word order), so a correction doesn't reject an answer the lesson accepted.
    val alternativeAnswers: List<String> = emptyList(),
    // WORD_SELECTION / TEXT_TRANSLATION's "(1 subject, 2 objects)" hint: without it a correction
    // can't tell which of several valid sentences is the expected one. null when the quiz had none.
    val subjectsCount: Int? = null,
    val objectsCount: Int? = null,
) {
    companion object {
        // The IQuizContent.id a quiz page carries is a fresh Uuid regenerated every time the
        // lesson's quiz content list is built (and partly shuffled), so it can't be used to
        // recognize "the same question" across sessions/devices. Callers must derive `id` from
        // stable, content-derived data instead — e.g. the lesson id plus a Gardiner code, or the
        // lesson id plus the correct-answer text — via this helper, so the same missed question
        // upserts (bumping `attempts`) rather than duplicating.
        fun buildId(lessonId: String, contentKey: String): String = "$lessonId::$contentKey"

        // Maximum number of saved mistakes replayed in a single correction batch (see
        // QuizViewModel's LessonIds.CORRECTION branch). Not a minimum: ErroriListContent's
        // "Correggi" button is enabled as soon as there's at least one correctable mistake.
        const val CORRECTION_BATCH_SIZE = 10
    }
}
